import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { compose } from 'recompose';
import { FieldArray } from 'redux-form';
import Sortable from 'sortablejs';
import some from 'lodash/some';
import { t } from '../../../../modules/infra/i18n';
import Tooltip from '../../../common/tooltip';
import EmptyList from './EmptyList';
import withLoadingList from '../../../common/hoc/withLoaingList';
import Icon, { GLYPHS } from '../../../common/icon';
import CheckboxSwitch from '../../../common/checkboxSwitch';
import BasicFormField from '../../../common/formFields/basicFormField';
import Switch from '../../../common/Switch';
import { isShopWithoutPaymentInfoSetup } from '../helpers';

class BrandShopList extends Component {
  constructor(props) {
    super(props);
    this.sortableInstance = null;
    this.isDragging = false;
    this.dragTimeout = null;
    this.state = {
      toggleMap: props.brandShops.map(shop => !!shop.overrideBookingLink),
    };
  }

  componentDidMount() {
    if (this.sortable) {
      this.setupSortable();
    }
  }

  onStart = () => {
    this.isDragging = true;
    clearTimeout(this.dragTimeout);
  }

  onEnd = () => {
    this.isDragging = false;
  }

  onSort = (e) => {
    const { brandShops, onSortChange } = this.props;
    this.dragTimeout = setTimeout(() => {
      if (this.isDragging) return;
      const toggleMap = this.arrayMove([...this.state.toggleMap], e.oldIndex, e.newIndex);
      this.setState({ toggleMap });
      const sorted = this.arrayMove([...brandShops], e.oldIndex, e.newIndex);
      const mapped = sorted.map((prev, index) => ({ ...prev, shopOrder: index }));
      onSortChange(mapped);
    }, 600);
  }

  onToggle = index => (value) => {
    const { brandShops, onSortChange } = this.props;
    const toggleMap = [...this.state.toggleMap];
    toggleMap[index] = value;
    this.setState({ toggleMap });
    if (!value) {
      brandShops[index].overrideBookingLink = null;
    }
    onSortChange(brandShops);
  };

  setupSortable() {
    this.sortableInstance = Sortable.create(this.sortable, {
      group: '', // To drag elements from one list into another, both lists must have the same group value
      handle: '.sortable-handle', // Drag handle selector within list items
      forceFallback: true, // Ignore the HTML5 DnD behaviour and force the fallback to kick in
      draggable: '.c-table-card__row', // Specifies which items inside the element should be draggable
      onStart: this.onStart,
      onEnd: this.onEnd,
      onSort: this.onSort,
      dragClass: '.c-table-card',
    });
  }

  arrayMove = (arr, fromIndex, toIndex) => {
    const element = arr[fromIndex];
    arr.splice(fromIndex, 1);
    arr.splice(toIndex, 0, element);
    return arr;
  }

  renderShops = ({ fields, brandShops, toggleMap, onToggle }) => (
    <div className="c-table-card c-table-new c-table-card--fixed u--mt-" ref={(c) => { this.sortable = c; }}>
      {fields.map((field, index) => (
        <div className="c-table-card__row is-sortable" key={brandShops[index].shopId}>
          <div>
            <div className="c-table-card__cell u-1/12 u-4/16-large-and-up">
              <div className="c-link-blank u-text-right u-pr- u-align-middle sortable-handle">
                <Icon glyph={GLYPHS.dragDrop} />
              </div>
            </div>
            <div className="c-table-card__cell u-3/12 u-3/16-large-and-up">
              {brandShops[index].name}
            </div>
            <div className="c-table-card__cell u-2/12 u-3/16-large-and-up">
              <CheckboxSwitch name={`${field}.showOnBrandPage`} id={`${field}.showOnBrandPage`} />
            </div>
            <div className="c-table-card__cell u-2/12 u-3/16-large-and-up">
              <CheckboxSwitch name={`${field}.shareGiftCards`} id={`${field}.shareGiftCards`} />
            </div>
            <div className="c-table-card__cell u-2/12 u-3/16-large-and-up">
              <div className="o-flexbox o-flexbox--no-wrap o-flexbox--content-align-center">
                <CheckboxSwitch
                  disabled={isShopWithoutPaymentInfoSetup(brandShops[index])}
                  name={`${field}.showOnCustomApp`}
                  id={`${field}.showOnCustomApp`}
                />
                {isShopWithoutPaymentInfoSetup(brandShops[index]) && (
                  <Tooltip className="c-info-tooltip-new--form c-info-tooltip-new--prohibited" >
                    {t('brand:form.brand_shop_config_section.disabled_booking_apps_tooltip')}
                  </Tooltip>
                )}
              </div>
            </div>
            <div className="c-table-card__cell u-2/12 u-3/16-large-and-up">
              <Switch
                checked={toggleMap[index]}
                onChange={onToggle(index)}
              />
            </div>
          </div>
          <div className="u-text-center" display-if={toggleMap[index]}>
            <BasicFormField
              placeholder={t('brand:form.brand_shop_config_section.override_booking_link_placeholder')}
              label={t('brand:form.brand_shop_config_section.override_booking_link_label')}
              name={`${field}.overrideBookingLink`}
              maxLength="255"
              normalize={value => (value || null)}
            />
          </div>
        </div>
        ))}
    </div>
    )

  render() {
    const { name, brandShops } = this.props;
    if (!some(brandShops, shop => shop.name)) return null;

    return (
      <div>
        <div className="c-table-card c-table-new c-table-card--fixed">
          <div className="c-table-card__head">
            <div className="c-table-card__cell c-label u-1/12 u-1/16-large-and-up">{t('brand:form.brand_shop_config_section.sort_header')}</div>
            <div className="c-table-card__cell c-label u-3/12 u-4/16-large-and-up">{t('brand:form.brand_shop_config_section.shop_name_header')}</div>
            <div className="c-table-card__cell c-label u-2/12 u-3/16-large-and-up">{t('brand:form.brand_shop_config_section.brand_page_header')}</div>
            <div className="c-table-card__cell c-label u-2/12 u-2/16-large-and-up">{t('brand:form.brand_shop_config_section.gift_cards_header')}</div>
            <div className="c-table-card__cell c-label u-2/12 u-3/16-large-and-up">
              {t('brand:form.brand_shop_config_section.booking_apps_header')}
              <Tooltip className="c-info-tooltip-new--form u-type--no-transform">
                {t('brand:form.brand_shop_config_section.booking_apps_tooltip')}
              </Tooltip>
            </div>
            <div className="c-table-card__cell c-label u-2/12 u-3/16-large-and-up">{t('brand:form.brand_shop_config_section.override_booking_link_header')}</div>
          </div>
        </div>
        <FieldArray
          name={name}
          component={this.renderShops}
          brandShops={brandShops}
          toggleMap={this.state.toggleMap}
          onToggle={this.onToggle}
        />
      </div>
    );
  }
}

BrandShopList.propTypes = {
  name: PropTypes.string.isRequired,
  brandShops: PropTypes.array.isRequired, // eslint-disable-line
  onSortChange: PropTypes.func.isRequired,
};

export default compose(
  withLoadingList(
    props => props.brandShops,
    EmptyList,
  ),
)(BrandShopList);
